// Smaller resources collected here: audit, alerts, billing, api_keys, me,
// permissions, slack. Big enough to be useful as hooks; small enough that a
// dedicated file per resource is overkill.

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { keys } from "../queryKeys.js";
import * as auditApi from "../api/audit.js";
import * as alertsApi from "../api/alerts.js";
import * as apiKeysApi from "../api/api_keys.js";
import * as billingApi from "../api/billing.js";
import * as meApi from "../api/me.js";
import * as permissionsApi from "../api/permissions.js";
import * as slackApi from "../api/slack.js";

// ── Audit ────────────────────────────────────────────────
export function useAuditLogs(params, options = {}) {
  return useQuery({
    queryKey: keys.auditLog.list(params),
    queryFn: () => auditApi.fetchAuditLogs(params),
    ...options,
  });
}

export function useAuditLogsInfinite(filters, options = {}) {
  return useInfiniteQuery({
    queryKey: keys.auditLog.list(filters),
    queryFn: ({ pageParam }) => {
      const params = { limit: 50, ...filters };
      if (pageParam) params.cursor = pageParam;
      return auditApi.fetchAuditLogs(params);
    },
    initialPageParam: null,
    getNextPageParam: (last) =>
      last?.meta?.has_next ? last.meta.next_cursor : undefined,
    ...options,
  });
}

// ── Alerts ───────────────────────────────────────────────
export function useAlertRules(monitorId, options = {}) {
  return useQuery({
    queryKey: keys.monitors.alertRules(monitorId),
    queryFn: () => alertsApi.fetchAlertRules(monitorId),
    enabled: !!monitorId,
    ...options,
  });
}

export function useAlertNotifications(monitorId, options = {}) {
  return useQuery({
    queryKey: ["monitor", monitorId, "alert-notifications"],
    queryFn: () => alertsApi.fetchAlertNotifications(monitorId),
    enabled: !!monitorId,
    ...options,
  });
}

export function useCreateAlertRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ monitorId, data }) => alertsApi.createAlertRule(monitorId, data),
    onSuccess: (_d, { monitorId }) =>
      qc.invalidateQueries({ queryKey: keys.monitors.alertRules(monitorId) }),
  });
}

export function useDeleteAlertRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ monitorId, ruleId }) =>
      alertsApi.deleteAlertRule(monitorId, ruleId),
    onSuccess: (_d, { monitorId }) =>
      qc.invalidateQueries({ queryKey: keys.monitors.alertRules(monitorId) }),
  });
}

// ── API keys ─────────────────────────────────────────────
export function useApiKeys(options = {}) {
  return useQuery({
    queryKey: keys.apiKeys.list,
    queryFn: apiKeysApi.listApiKeys,
    ...options,
  });
}

export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: apiKeysApi.createApiKey,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.apiKeys.all }),
  });
}

export function useDeleteApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: apiKeysApi.deleteApiKey,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.apiKeys.all }),
  });
}

// ── Billing ──────────────────────────────────────────────
export function useBillingPlans(options = {}) {
  return useQuery({
    queryKey: ["billing", "plans"],
    queryFn: billingApi.fetchBillingPlans,
    staleTime: 60 * 60_000, // plans don't change often
    ...options,
  });
}

export function useBillingStatus(options = {}) {
  return useQuery({
    queryKey: keys.billing.current,
    queryFn: billingApi.fetchBillingStatus,
    ...options,
  });
}

export function useBillingWorkspaces(options = {}) {
  return useQuery({
    queryKey: ["billing", "workspaces"],
    queryFn: billingApi.fetchBillingWorkspaces,
    ...options,
  });
}

export function useCancelSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: billingApi.cancelSubscription,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.billing.all }),
  });
}

// ── Me ───────────────────────────────────────────────────
export function useMyWorkspaces(options = {}) {
  return useQuery({
    queryKey: ["my-workspaces"],
    queryFn: meApi.fetchMyWorkspaces,
    ...options,
  });
}

export function useMe(options = {}) {
  return useQuery({
    queryKey: keys.me.current,
    queryFn: meApi.fetchMe,
    ...options,
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: meApi.updateMe,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me.current }),
  });
}

export function useUpdateMyPassword() {
  return useMutation({ mutationFn: meApi.updateMyPassword });
}

export function useStartTOTPSetup() {
  return useMutation({ mutationFn: meApi.startTOTPSetup });
}

export function useEnableTOTP() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: meApi.enableTOTP,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me.current }),
  });
}

export function useDisableTOTP() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: meApi.disableTOTP,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me.current }),
  });
}

export function useSetEmailOTP() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: meApi.setEmailOTP,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me.current }),
  });
}

export function useRegenerateRecoveryCodes() {
  return useMutation({ mutationFn: meApi.regenerateRecoveryCodes });
}

// ── Permissions ──────────────────────────────────────────
export function useMemberPermissions(userId, options = {}) {
  return useQuery({
    queryKey: ["permissions", "member", userId],
    queryFn: () => permissionsApi.getMemberPermissions(userId),
    enabled: !!userId,
    ...options,
  });
}

export function useSetPermissionOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, permission, effect }) =>
      permissionsApi.setPermissionOverride(userId, permission, effect),
    onSuccess: (_d, { userId }) =>
      qc.invalidateQueries({ queryKey: ["permissions", "member", userId] }),
  });
}

export function useDeletePermissionOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, permission }) =>
      permissionsApi.deletePermissionOverride(userId, permission),
    onSuccess: (_d, { userId }) =>
      qc.invalidateQueries({ queryKey: ["permissions", "member", userId] }),
  });
}

// ── Slack ────────────────────────────────────────────────
export function useSlackInstallation(options = {}) {
  return useQuery({
    queryKey: keys.slack.connection,
    queryFn: slackApi.fetchSlackInstallation,
    ...options,
  });
}

export function useUpdateSlackInstallation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: slackApi.updateSlackInstallation,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.slack.connection }),
  });
}

export function useDeleteSlackInstallation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: slackApi.deleteSlackInstallation,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.slack.connection }),
  });
}
